import type React from 'react';
import { useCallback } from 'react';

import * as Styled from './ContentSwitcher.styled';

export type ContentSwitcherOption<T extends string = string> = {
  value: T;
  label?: string;
  icon?: React.ReactNode;
  ariaLabel: string;
};

export type ContentSwitcherProps<T extends string = string> = {
  options: ContentSwitcherOption<T>[];
  value: T;
  onChange: (value: T) => void;
  inverse?: boolean;
  size?: 'regular' | 'small';
  disabled?: boolean;
  ariaLabel?: string;
};

function ContentSwitcher<T extends string = string>({
  options,
  value,
  onChange,
  inverse = false,
  size = 'regular',
  disabled = false,
  ariaLabel = 'Content switcher',
}: ContentSwitcherProps<T>) {
  const handleSelect = useCallback(
    (optionValue: T) => {
      if (!disabled && optionValue !== value) {
        onChange(optionValue);
      }
    },
    [disabled, value, onChange],
  );

  return (
    <Styled.Container $inverse={inverse} aria-label={ariaLabel} role="group">
      <Styled.Grouping $inverse={inverse}>
        {options.map(option => {
          const isSelected = option.value === value;

          return (
            <Styled.Option
              key={option.value}
              $inverse={inverse}
              $selected={isSelected}
              $size={size}
              aria-label={option.ariaLabel}
              aria-pressed={isSelected}
              disabled={disabled}
              type="button"
              onClick={() => handleSelect(option.value)}
            >
              {option.icon}
              {option.label && <span>{option.label}</span>}
            </Styled.Option>
          );
        })}
      </Styled.Grouping>
    </Styled.Container>
  );
}

export default ContentSwitcher;
